import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Common
    loading: 'Chargement...',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    search: 'Rechercher',
    filter: 'Filtrer',
    actions: 'Actions',
    status: 'Statut',
    date: 'Date',
    amount: 'Montant',
    currency: 'Devise',
    description: 'Description',
    confirm: 'Confirmer',
    back: 'Retour',
    next: 'Suivant',
    submit: 'Soumettre',
    close: 'Fermer',
    
    // Auth
    login: 'Connexion',
    logout: 'Déconnexion',
    email: 'Email',
    password: 'Mot de passe',
    new_password: 'Nouveau mot de passe',
    confirm_password: 'Confirmer le mot de passe',
    forgot_password: 'Mot de passe oublié ?',
    remember_me: 'Se souvenir de moi',
    sign_in: 'Se connecter',
    first_login_title: 'Première connexion',
    first_login_subtitle: 'Veuillez changer votre mot de passe provisoire',
    current_password: 'Mot de passe actuel',
    pin_setup_title: 'Configuration du PIN',
    pin_setup_subtitle: 'Sécurisez votre compte avec un code PIN',
    enter_pin: 'Entrez votre PIN',
    
    // Navigation
    dashboard: 'Tableau de bord',
    clients: 'Clients',
    portfolios: 'Portefeuilles',
    products: 'Produits',
    contracts: 'Contrats',
    trading: 'Trading',
    transactions: 'Transactions',
    documents: 'Documents',
    messages: 'Messages',
    notifications: 'Notifications',
    settings: 'Paramètres',
    profile: 'Profil',
    audit: 'Journal d\'audit',
    branding: 'Personnalisation',
    template: 'Template client',
    funds: 'Gestion des fonds',
    
    // Dashboard
    welcome: 'Bienvenue',
    total_capital: 'Capital total',
    available_balance: 'Solde disponible',
    active_contracts: 'Contrats actifs',
    pending_requests: 'Demandes en attente',
    total_aum: 'Actifs sous gestion',
    total_clients: 'Total clients',
    pending_deposits: 'Dépôts en attente',
    pending_withdrawals: 'Retraits en attente',
    pending_kyc: 'KYC en attente',
    active_simulations: 'Simulations actives',
    
    // Portfolios
    portfolio_available: 'Portefeuille disponible',
    portfolio_trade: 'Portefeuille Trade',
    portfolio_stock: 'Portefeuille Bourse',
    portfolio_crypto: 'Portefeuille Crypto',
    capital: 'Capital',
    available: 'Disponible',
    bonus: 'Bonus',
    
    // Funds
    deposit: 'Dépôt',
    withdrawal: 'Retrait',
    request_deposit: 'Demander un dépôt',
    request_withdrawal: 'Demander un retrait',
    approve: 'Approuver',
    reject: 'Refuser',
    pending: 'En attente',
    approved: 'Approuvé',
    rejected: 'Refusé',
    
    // KYC
    kyc: 'Vérification KYC',
    identity_document: 'Pièce d\'identité',
    proof_of_address: 'Justificatif de domicile',
    upload_document: 'Téléverser un document',
    under_review: 'En cours de vérification',
    
    // Products
    savings_products: 'Produits d\'épargne',
    min_investment: 'Investissement minimum',
    max_investment: 'Investissement maximum',
    return_rate: 'Taux de rendement',
    risk_level: 'Niveau de risque',
    subscribe: 'Souscrire',
    low: 'Faible',
    medium: 'Moyen',
    high: 'Élevé',
    
    // Contracts
    sign_contract: 'Signer le contrat',
    signature: 'Signature',
    signed: 'Signé',
    active: 'Actif',
    terminated: 'Résilié',
    pending_signature: 'En attente de signature',
    
    // Trading
    trading: 'Trading',
    create_simulation: 'Créer une stratégie',
    positions: 'Positions',
    profit_loss: 'Profit/Perte',
    winning_trades: 'Trades gagnants',
    losing_trades: 'Trades perdants',
    total_profit: 'Profit total',
    
    // Client
    client_code: 'Code client',
    date_of_birth: 'Date de naissance',
    place_of_birth: 'Lieu de naissance',
    nationality: 'Nationalité',
    address: 'Adresse',
    phone: 'Téléphone',
    assigned_analyst: 'Analyste assigné',
    notes: 'Notes',
    appointments: 'Rendez-vous',
    
    // Admin
    platform_settings: 'Paramètres de la plateforme',
    platform_name: 'Nom de la plateforme',
    logo: 'Logo',
    primary_color: 'Couleur principale',
    secondary_color: 'Couleur secondaire',
    accent_color: 'Couleur d\'accent',
    dark_mode: 'Mode sombre',
    legal_notice: 'Mentions légales',
    privacy_policy: 'Politique de confidentialité',
    terms_of_service: 'Conditions d\'utilisation',
    
    // Template
    client_template: 'Template d\'expérience client',
    authentication: 'Authentification',
    pin_enabled: 'PIN activé',
    pin_length: 'Longueur du PIN',
    session_timeout: 'Délai d\'inactivité',
    menu_visibility: 'Visibilité des menus',
    dashboard_widgets: 'Widgets du tableau de bord',
    trading_settings: 'Paramètres de trading',
    manual_trading: 'Trading manuel',
    auto_trading: 'Trading automatique',
    max_leverage: 'Levier maximum',
    max_exposure: 'Exposition maximum',
    
    // IBAN
    iban: 'IBAN',
    bic: 'BIC',
    bank_name: 'Nom de la banque',
    account_holder: 'Titulaire du compte',
    create_iban: 'Créer un IBAN',
    download_pdf: 'Télécharger PDF',
    
    // Messages
    compose: 'Composer',
    inbox: 'Boîte de réception',
    sent: 'Envoyés',
    subject: 'Sujet',
    message: 'Message',
    send: 'Envoyer',
    reply: 'Répondre',
    
    // Mirror Mode
    mirror_mode: 'Mode Vue Client',
    exit_mirror: 'Quitter la vue client',
    login_as_client: 'Se connecter comme le client',
    
    // Misc
    no_data: 'Aucune donnée',
    error: 'Erreur',
    success: 'Succès',
    warning: 'Attention',
    info: 'Information'
  },
  en: {
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    amount: 'Amount',
    currency: 'Currency',
    description: 'Description',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    close: 'Close',
    
    // Auth
    login: 'Login',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    new_password: 'New password',
    confirm_password: 'Confirm password',
    forgot_password: 'Forgot password?',
    remember_me: 'Remember me',
    sign_in: 'Sign in',
    first_login_title: 'First Login',
    first_login_subtitle: 'Please change your temporary password',
    current_password: 'Current password',
    pin_setup_title: 'PIN Setup',
    pin_setup_subtitle: 'Secure your account with a PIN code',
    enter_pin: 'Enter your PIN',
    
    // Navigation
    dashboard: 'Dashboard',
    clients: 'Clients',
    portfolios: 'Portfolios',
    products: 'Products',
    contracts: 'Contracts',
    trading: 'Trading',
    transactions: 'Transactions',
    documents: 'Documents',
    messages: 'Messages',
    notifications: 'Notifications',
    settings: 'Settings',
    profile: 'Profile',
    audit: 'Audit Log',
    branding: 'Branding',
    template: 'Client Template',
    funds: 'Fund Management',
    
    // Dashboard
    welcome: 'Welcome',
    total_capital: 'Total Capital',
    available_balance: 'Available Balance',
    active_contracts: 'Active Contracts',
    pending_requests: 'Pending Requests',
    total_aum: 'Assets Under Management',
    total_clients: 'Total Clients',
    pending_deposits: 'Pending Deposits',
    pending_withdrawals: 'Pending Withdrawals',
    pending_kyc: 'Pending KYC',
    active_simulations: 'Active Simulations',
    
    // Portfolios
    portfolio_available: 'Available Portfolio',
    portfolio_trade: 'Trade Portfolio',
    portfolio_stock: 'Stock Portfolio',
    portfolio_crypto: 'Crypto Portfolio',
    capital: 'Capital',
    available: 'Available',
    bonus: 'Bonus',
    
    // Funds
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    request_deposit: 'Request Deposit',
    request_withdrawal: 'Request Withdrawal',
    approve: 'Approve',
    reject: 'Reject',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    
    // KYC
    kyc: 'KYC Verification',
    identity_document: 'Identity Document',
    proof_of_address: 'Proof of Address',
    upload_document: 'Upload Document',
    under_review: 'Under Review',
    
    // Products
    savings_products: 'Savings Products',
    min_investment: 'Minimum Investment',
    max_investment: 'Maximum Investment',
    return_rate: 'Return Rate',
    risk_level: 'Risk Level',
    subscribe: 'Subscribe',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    
    // Contracts
    sign_contract: 'Sign Contract',
    signature: 'Signature',
    signed: 'Signed',
    active: 'Active',
    terminated: 'Terminated',
    pending_signature: 'Pending Signature',
    
    // Trading
    trading: 'Trading',
    create_simulation: 'Create Strategy',
    positions: 'Positions',
    profit_loss: 'Profit/Loss',
    winning_trades: 'Winning Trades',
    losing_trades: 'Losing Trades',
    total_profit: 'Total Profit',
    
    // Client
    client_code: 'Client Code',
    date_of_birth: 'Date of Birth',
    place_of_birth: 'Place of Birth',
    nationality: 'Nationality',
    address: 'Address',
    phone: 'Phone',
    assigned_analyst: 'Assigned Analyst',
    notes: 'Notes',
    appointments: 'Appointments',
    
    // Admin
    platform_settings: 'Platform Settings',
    platform_name: 'Platform Name',
    logo: 'Logo',
    primary_color: 'Primary Color',
    secondary_color: 'Secondary Color',
    accent_color: 'Accent Color',
    dark_mode: 'Dark Mode',
    legal_notice: 'Legal Notice',
    privacy_policy: 'Privacy Policy',
    terms_of_service: 'Terms of Service',
    
    // Template
    client_template: 'Client Experience Template',
    authentication: 'Authentication',
    pin_enabled: 'PIN Enabled',
    pin_length: 'PIN Length',
    session_timeout: 'Session Timeout',
    menu_visibility: 'Menu Visibility',
    dashboard_widgets: 'Dashboard Widgets',
    trading_settings: 'Trading Settings',
    manual_trading: 'Manual Trading',
    auto_trading: 'Auto Trading',
    max_leverage: 'Max Leverage',
    max_exposure: 'Max Exposure',
    
    // IBAN
    iban: 'IBAN',
    bic: 'BIC',
    bank_name: 'Bank Name',
    account_holder: 'Account Holder',
    create_iban: 'Create IBAN',
    download_pdf: 'Download PDF',
    
    // Messages
    compose: 'Compose',
    inbox: 'Inbox',
    sent: 'Sent',
    subject: 'Subject',
    message: 'Message',
    send: 'Send',
    reply: 'Reply',
    
    // Mirror Mode
    mirror_mode: 'Client View Mode',
    exit_mirror: 'Exit Client View',
    login_as_client: 'Login as Client',
    
    // Misc
    no_data: 'No data',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Information'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['fr'][key] || key;
  };

  const changeLanguage = (lang) => {
    if (lang === 'fr' || lang === 'en') {
      setLanguage(lang);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    translations: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
